/*
 * Markdown export — ported from `buildPlanMarkdown` in
 * artifacts/prototypes/motr-improvement-plan-v1.html (~3192–3270).
 *
 * Used by the Copy-as-Markdown action on the results screen. The
 * shape and section ordering is preserved verbatim because some
 * buyers paste the export into a doc and the existing prototype is
 * the reference.
 */
import type { PlanBuilderConfig } from './config';
import type { DiagnosticAnswers, MatchedAction, ScoreResult } from './types';

export function buildPlanMarkdown(
  s: ScoreResult,
  answers: DiagnosticAnswers,
  matched: MatchedAction[],
  config: PlanBuilderConfig,
): string {
  const blocker = answers.gap2 ? config.planEngine.blockers[answers.gap2] : null;
  const L: string[] = [];

  L.push('# Legal Operations Improvement Plan');
  L.push('*A Reset-shaped plan, calibrated to your team.*');
  L.push('');
  L.push(`**Stage:** ${s.stage} — ${s.stageData.name}`);
  L.push(`**Joy in Work score:** ${s.joyScore}/100`);
  if (s.alignmentGap) L.push('**Management alignment gap detected.**');
  L.push('');
  L.push('---');
  L.push('');
  L.push('## Part 1 — Your Leverage Points');
  L.push('*Where a little bit of effort yields a disproportionate return.*');
  L.push('');
  L.push(`### Map the System — Stage ${s.stage}, ${s.stageData.name}`);
  L.push(s.stageData.desc);
  L.push('');
  if (blocker) {
    L.push(`### Target the Constraint — ${blocker.label}`);
    L.push(blocker.body);
    L.push('');
  }
  L.push(`### Study the Bright Spots — ${config.scoring.ihiLabels[s.strongest]}`);
  L.push(
    "Your strongest foundation. Use this as the anchor for your improvement effort. Ask: what already works here, and can any of it be transplanted to the places that aren't working?",
  );
  L.push('');

  const miracleAnswers = (
    ['miracle_desk', 'miracle_midday', 'miracle_gc'] as const
  )
    .map((k) => answers[k] && String(answers[k]).trim())
    .filter((s): s is string => Boolean(s));

  if (miracleAnswers.length) {
    L.push("### Goal of the Goal — what you're moving toward");
    L.push('You said the morning after the miracle would look like:');
    L.push('');
    miracleAnswers.forEach((q) => L.push(`> *${q}*`));
    L.push('');
    L.push(
      'Hold this. The plan below is the smallest set of moves we think most likely to get you closer to that morning.',
    );
    L.push('');
  } else {
    L.push(
      '> **Goal of the Goal.** What is the team *ultimately* trying to deliver? If you achieved perfect operational maturity and nothing else changed, would the business be better off? Sometimes "stronger legal ops" is the wrong target — and a smaller, different thing is the real goal.',
    );
    L.push('');
  }
  L.push('---');
  L.push('');
  L.push('## Part 2 — Your Resource Stack');
  L.push('*How to reallocate time, effort, and attention onto those leverage points.*');
  L.push('');
  matched.forEach((m, i) => {
    const a = m.action;
    const lever = config.planEngine.resetLevers[a.resetLever];
    L.push(`### ${i + 1}. ${a.title}`);
    L.push(`*${lever.name} · ${a.effort} effort · ${a.horizon}*`);
    L.push('');
    L.push(`**Why this, for your team:** ${m.reasons.join('; ')}.`);
    L.push('');
    L.push(a.body);
    L.push('');
    L.push(`*Evidence: ${a.research}*`);
    L.push('');
  });
  L.push(
    '> **Do Less and Do More.** Before starting any of the above, commit to reducing or removing one thing currently consuming team time without moving a goal. New work without stopped work is just a bigger pile.',
  );
  L.push('');
  if (s.preMortem && config.planEngine.premortems[s.preMortem]) {
    L.push('---');
    L.push('');
    L.push('## Pre-Mortem — Your Most Likely Failure Mode');
    L.push(`**Risk:** ${config.planEngine.premortems[s.preMortem].risk}`);
    L.push('');
    L.push(`**How to address it:** ${config.planEngine.premortems[s.preMortem].mit}`);
    L.push('');
  }
  L.push('---');
  L.push('');
  L.push('*Generated by the mot-r Legal Operations Improvement Plan.*');
  L.push(
    '*Built on Heath Reset (2025) · IHI Joy in Work (Perlo et al., 2017) · Klein Pre-Mortem (HBR, 2007) · Pfeffer & Sutton evidence-based management.*',
  );
  return L.join('\n');
}
